<template>
  <div class="drawer">
    <v-navigation-drawer
      v-model="mini"
      app
      permanent
      :class="{ collapseMenu: mini }"
    >
      <v-list-item link>
        <v-list-item-title class="drawer__logo d-flex justify-center">
          <LogoDrawerShortIcon v-if="mini" width="40" height="40" />
          <template v-else>
            <template v-if="subscriptionFeatures.productName && subscriptionFeatures.productName.includes('Reports Only')">
              <LogoDrawerReportsOnlyIcon width="156" />
            </template>
            <template v-else-if="subscriptionFeatures.productName && subscriptionFeatures.productName.includes('Survey Only')">
              <LogoDrawerSurveyOnlyIcon width="156" />
            </template>
            <template v-else-if="subscriptionFeatures.productName && subscriptionFeatures.productName.includes('Patient Portal')">
              <LogoDrawerCompleteIcon width="156" />
            </template>
            <template v-else>
              <LogoDrawerIcon width="156" height="46" />
            </template>
          </template>
        </v-list-item-title>
      </v-list-item>
      <v-list nav dense>
        <template v-for="(item, i) in filteredItemsOnlyAuth">
          <v-tooltip
            right
            :disabled="!mini"
            :key="i"
            nudge-right="6"
            content-class="drawer-tooltip"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-list-item
                @click="goToByLink(item)"
                :class="{'active-tab': isActive(item)}"
                :disabled="subscriptionFeatures.expired && item.name !== 'Collapse'"
                v-bind="attrs"
                v-on="on"
              >
                <v-list-item-icon class="align-self-center">
                  <template v-if="item.name === 'Patients'">
                    <v-badge
                      style="display: flex"
                      color="#7cdbc1"
                      v-if="totalCountPatients && totalCountPatients.pagination"
                      :value="totalCountPatients.pagination.total"
                      :content="totalCountPatients.pagination.total"
                      overlap
                      offset-x="10"
                      offset-y="25"
                    >
                      <img
                        :src="getIconSrc(item)"
                        class="menu-svg-icon"
                        :class="{ rotateIcon: !item.link && mini }"
                        alt="icon"
                      />
                    </v-badge>
                    <img
                      v-else
                      :src="getIconSrc(item)"
                      class="menu-svg-icon"
                      :class="{ rotateIcon: !item.link && mini }"
                      alt="icon"
                    />
                  </template>
                  <template v-else>
                    <img
                      :src="getIconSrc(item)"
                      class="menu-svg-icon"
                      :class="{ rotateIcon: !item.link && mini }"
                      alt="icon"
                    />
                  </template>
                </v-list-item-icon>
                <v-list-item-title style="font-size: 14px; color: #425466" :class="{hideMenuTitles: mini}">
                  {{item.name}}
                </v-list-item-title>
              </v-list-item>
            </template>
            <span>{{ item.name }}</span>
          </v-tooltip>
        </template>
      </v-list>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { trackEvent } from '@/utils/analytics'

import LogoDrawerShortIcon from '@/assets/icons/components/logo_drawer_short.svg'
import LogoDrawerReportsOnlyIcon from '@/assets/icons/components/logo_drawer_reports-only.svg'
import LogoDrawerSurveyOnlyIcon from '@/assets/icons/components/logo_drawer_survey-only.svg'
import LogoDrawerCompleteIcon from '@/assets/icons/components/logo_drawer_complete.svg'
import LogoDrawerIcon from '@/assets/icons/components/logo_drawer.svg'

export default {
  name: 'drawer',
  props: {
    items: {
      type: Array
    }
  },
  components: {
    LogoDrawerShortIcon,
    LogoDrawerReportsOnlyIcon,
    LogoDrawerSurveyOnlyIcon,
    LogoDrawerCompleteIcon,
    LogoDrawerIcon
  },
  data: () => ({
    mini: true
  }),
  mounted () {
    const savedState = localStorage.getItem('drawerState')
    const newMini = savedState !== null ? !JSON.parse(savedState) : true

    this.$nextTick(() => {
      this.mini = newMini
    })

    const { successPayment, tab, modal } = this.$route.query
    if (this.userProfile.firstLogin) {
      this.$router.push('/settings')
    } else if (!this.userProfile.firstLogin && !successPayment && !tab && !this.isAuth) {
      this.$router.push({ name: 'Home' })
    } else if (this.isAuth && tab) {
      if (!modal) {
        this.$router.push(`/settings?tab=${tab}`)
      }
      if (modal) {
        this.$router.push(`/settings?tab=${tab}&modal=${modal}`)
      }
    } else if (this.isAuth && !successPayment && sessionStorage.getItem('selectedPage') && this.$route.path !== sessionStorage.getItem('selectedPage')) {
      this.$router.push(sessionStorage.getItem('selectedPage'))
    } else if (this.isAuth && (successPayment === '1' || successPayment === '0')) {
      this.$router.push(`/settings?successPayment=${successPayment}`)
    }
  },
  methods: {
    ...mapMutations('dialogs', ['TOGGLE_DIALOG']),
    goToByLink (item) {
      if (item.name === 'User Guide') {
        trackEvent(this, 'user_guide', 'Guide', 'User Guide Was Clicked')
        window.open(item.link, '_blank')
        return
      }
      if (!this.subscriptionFeatures.expired) {
        if (item.link && item.link !== this.$route.path) {
          this.$router.push(item.link)
          this.saveCurrentPage(item.link)
        }
      }
      if (!item.link) {
        this.toggleCollapseMenu()
      }
    },
    saveCurrentPage (page) {
      sessionStorage.setItem('selectedPage', page)
    },
    toggleCollapseMenu () {
      this.mini = !this.mini
      localStorage.setItem('drawerState', JSON.stringify(!this.mini))
      this.$emit('toggleDrawer', this.mini)
    },
    isActive(item) {
      if (!item.link) return false;
      return this.$route.path === item.link
    },
    getIconSrc(item) {
      const active = this.isActive(item);
      return active ? item.activeIcon || item.icon : item.icon;
    },
  },
  computed: {
    ...mapState({
      isAuth: state => state.isAuth,
      totalCountPatients: state => state.patients.patients,
      userProfile: state => state.userProfile,
      subscriptionFeatures: state => state.subscription
    }),
    filteredItemsOnlyAuth () {
      return this.items.filter(item => {
        return ((!item.onlyAuth) || (item.onlyAuth === this.isAuth))
      })
    }
  }
}
</script>

<style lang="scss">
.v-tooltip__content.drawer-tooltip {
  font-family: Poppins;
  font-size: 16px !important;
  font-weight: 400 !important;
  color: #1A202C !important;
  line-height: 1.5 !important;
  background-color: #FFFFFF !important;
  border-radius: 8px !important;
  padding: 10px !important;
  box-shadow: 0px 2px 10px 0px rgba(25, 37, 55, 0.17) !important;
  opacity: 1 !important;
}
</style>

<style lang="scss" scoped>
  .drawer {
    ::v-deep .v-navigation-drawer {
      width: 180px!important;
      height: 100% !important;

      &__content {
        overflow-y: hidden;

        > .v-list-item {
          padding: 0;
          margin: 0;
          cursor: pointer;
          height: 90px;
          max-height: 90px;
        }

        .v-list {
          padding: 0 !important;
          margin: 0 !important;

          .v-list-item {
            position: relative;
            height: 56px;
            max-height: 56px;
            border-radius: inherit;
            margin: 0 !important;
            padding: 0 !important;

            &::before {
              content: none;
            }

            &:hover {
              background-color: #E5F3F3;
            }

            &--active {
              color: $dark-green;

              &::after {
                content: '' !important;
                position: absolute !important;
                left: 0 !important;
                height: 100% !important;
                width: 2px !important;
                background-color: $dark-green !important;
              }

              .v-list-item__title {
                color: $dark-green !important;
                font-weight: 500;
              }

              &::before {
                opacity: 0;
              }
            }
            &__icon {
              width: 56px;
              height: 56px;
              flex: 0 0 56px;
              justify-content: center;
              align-items: center;
              padding: 0 !important;
              margin: 0 !important;

              img {
                width: 24px;
                height: 24px;
              }

              .v-badge__badge {
                background-color: #E1E9FF !important;
                border-block-color: #E1E9FF !important;
                color: #537CFF;
                font-weight: 700;
                font-size: 11px;
                bottom: -13px !important;
                top: auto !important;
                right: auto !important;
                left: 8px !important;
              }
            }
          }
        }
      }
    }

    .count-patients {
      background-color: #E1E9FF;
      border-radius: 18px;
      padding: 1px 4px;
      display: inline-block;
      color: #537CFF;
      font-weight: 700;
      font-size: 11px;
    }

    .collapseMenu {
      width: 56px !important;
    }

    .hideMenuTitles {
      position: absolute !important;
      height: 1px;
      width: 1px;
      overflow: hidden;
      clip: rect(1px, 1px, 1px, 1px);
      white-space: nowrap;
      display: initial;
    }

    .rotateIcon {
      transform: rotateZ(180deg);
    }

    .active-tab {
      background-color: rgba(0, 140, 137, 0.1);
    }
  }
</style>
